// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import CourierTop from './CourierType/Top';
import ModalTop from './ModalType/Top';
import StandAloneTop from './StandAloneType/Top';
import MyAddressTop from './MyAddressType/Top';
import StandAloneBottom from './StandAloneType/Bottom';
import classNames from 'classnames/bind';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { FormValidator } from '../../utils/form';
import { getAddressesIntlIds } from '../../utils/address';
import Select from '../Select/Select';
import Loading from '../Loading/Loading';
import Cleave from 'cleave.js/react';
import { Element } from 'react-scroll/modules';

import inputStyle from '../../assets/styles/input.scss';
import formStyle from '../../assets/styles/form.scss';
import style from './AddressForm.scss';

import type { Option } from '../Select/Select';
import type { Country, Admin, Address } from '../../entities';
import type { LocalValuesState } from '../../modules/local-values/duck';

type Props = {
  +readOnly?: boolean;
  +addresses?: Address[];
  +isPutting?: boolean;
  +isPosting?: boolean;
  +handleSaveAddress?: (e: Event) => void;
  +handleChange: (e: SyntheticInputEvent<HTMLInputElement>) => void;
  +handleChangeAddressOption?: (option: Option) => void;
  +handleChangePhone: (e: Event) => void;
  +handleFocus: (e: Event) => void;
  +handleChangeCountry?: (option: Option) => void;
  +handleChangeAdmin1: (option: Option) => void;
  +handleChangeAdmin2: (option: Option) => void;
  +handleChangeAdmin3: (option: Option) => void;
  +handleSubmit?: (e: Event) => void;
  +handlePhoneInit: (cleave: any) => void;
  +country: {
    value: Country;
    errors?: string[];
  };
  +admin1: {
    value: string;
    label?: string;
    errors?: string[];
  };
  +admin1Items: Admin[];
  +isLoadingAdmin1?: boolean;
  +admin2: {
    value: string;
    label?: string;
    errors?: string[];
  };
  +admin2Items: Admin[];
  +isLoadingAdmin2?: boolean;
  +admin3: {
    value: string;
    label?: string;
    errors?: string[];
  };
  +admin3Items: Admin[];
  +isLoadingAdmin3?: boolean;
  +address1: {
    value: string;
    errors?: string[];
  };
  +address2: {
    value: string;
  };
  +firstName?: {
    value: string;
    errors?: string[];
  };
  +idNumber?: {
    value: string;
    errors?: string[];
  };
  +lastName?: {
    value: string;
    errors?: string[];
  };
  +name?: {
    value: string;
    errors?: string[];
  };
  +phone: {
    value: string;
    errors?: string[];
  };
  +saveAddress?: boolean;
  +detail?: {
    value: string;
  };
  +type: 'modal' | 'standAlone' | 'myAddress' | 'payout' | 'billing' | 'courier';
  +zipCode: {
    value: string;
    errors?: string[];
  };
  +localValues?: LocalValuesState;
  +addressOption?: {
    value: string;
    errors?: string[];
  };
};

const messages = defineMessages({
  name: {
    label: {
      id: 'preferences.savedAddresses.name.label',
    },
    labelCO: {
      id: 'preferences.savedAddresses.name.label.CO',
    },
    placeholder: {
      id: 'preferences.savedAddresses.name.placeholder',
    },
  },
  phone: {
    label: {
      id: 'address.phone.label',
    },
    placeholder: {
      id: 'address.phone.placeholder',
    },
  },
  zipCode: {
    label: {
      id: 'address.zipCode.label',
    },
    placeholder: {
      id: 'address.zipCode.placeholder',
    },
  },
});

export default class AddressForm extends Component<Props> {
  handleInnerSubmit(e: Event) {
    const { handleSubmit } = this.props;
    e.preventDefault();
    if (handleSubmit) {
      handleSubmit(e);
    }
  }

  render() {
    const {
      readOnly, addresses,
      isPutting, isPosting, handleSaveAddress, handleChange, handleChangeAddressOption, handleFocus, handleChangeCountry, handleChangePhone,
      handleChangeAdmin1, handleChangeAdmin2, handleChangeAdmin3, country, admin1, admin2, admin3, admin1Items,
      admin2Items, admin3Items, isLoadingAdmin1, isLoadingAdmin2, isLoadingAdmin3, address1, address2, firstName,
      lastName, idNumber, name, phone, detail = { value: '' }, type, zipCode, addressOption,
      saveAddress = false, handlePhoneInit, localValues,
    } = this.props;
    const countryIsPE = country.value === 'PE';
    const addressesIntlIds = getAddressesIntlIds(country.value);
    const admin1Options: Option[] = admin1Items && admin1Items.map((item: Admin) => ({
      value: item.id,
      label: item.name,
    }));
    const admin2Options: Option[] = admin2Items && admin2Items.map((item: Admin) => ({
      value: item.id,
      label: item.name,
    }));
    const admin3Options: Option[] = admin3Items && admin3Items.map((item: Admin) => ({
      value: item.id,
      label: item.name,
    }));
    const admin1Error = admin1.errors && FormValidator.buildErrorLabel(admin1.errors, {
      required: addressesIntlIds.admin1.error,
    });
    const admin2Error = admin2.errors && FormValidator.buildErrorLabel(admin2.errors, {
      required: addressesIntlIds.admin2.error,
    });
    const admin3Error = admin3.errors && addressesIntlIds.admin3 && FormValidator.buildErrorLabel(admin3.errors, {
      required: addressesIntlIds.admin3.error,
    });
    const address1Error = address1.errors && FormValidator.buildErrorLabel(address1.errors, {
      required: addressesIntlIds.address1.error,
    });
    const phoneError = phone.errors && FormValidator.buildErrorLabel(phone.errors, {
      required: 'address.phone.error.required',
    });
    const zipCodeError = zipCode && zipCode.errors && FormValidator.buildErrorLabel(zipCode.errors, {
      required: 'address.zipCode.error.required',
    });
    const defaultEmptyValue = { value: '' };
    let top;
    let bottom;
    switch (type) {
      case 'courier':
        top = <CourierTop key="top"
                          isPosting={isPosting}
                          handleChange={handleChange}
                          handleFocus={handleFocus}
                          readOnly={!!readOnly}
                          name={name || defaultEmptyValue}/>
        break;
      case 'modal':
        top = <ModalTop key="top"
                        isPutting={isPutting}
                        isPosting={isPosting}
                        handleChange={handleChange}
                        handleFocus={handleFocus}
                        handleChangeCountry={handleChangeCountry ? handleChangeCountry : () => {
                        }}
                        country={country}
                        firstName={firstName || defaultEmptyValue}
                        idNumber={idNumber || defaultEmptyValue}
                        lastName={lastName || defaultEmptyValue}
                        name={name || defaultEmptyValue}
                        localValues={localValues}/>;
        break;
      case 'standAlone':
        top = <StandAloneTop key="top"
                             isPosting={isPosting}
                             handleChange={handleChange}
                             handleFocus={handleFocus}
                             country={country.value}
                             firstName={firstName || defaultEmptyValue}
                             idNumber={idNumber || defaultEmptyValue}
                             lastName={lastName || defaultEmptyValue}/>;
        bottom = <StandAloneBottom key="bottom"
                                   isPosting={isPosting}
                                   handleSaveAddress={handleSaveAddress ? handleSaveAddress : () => {
                                   }}
                                   country={country.value}
                                   handleChange={handleChange}
                                   handleFocus={handleFocus}
                                   name={name || defaultEmptyValue}
                                   saveAddress={saveAddress}/>;
        break;
      case 'myAddress':
        top = <MyAddressTop key="top"
                            addresses={addresses}
                            isPosting={isPosting}
                            handleChange={handleChange}
                            handleChangeAddressOption={handleChangeAddressOption}
                            handleFocus={handleFocus}
                            country={country.value}
                            firstName={firstName || defaultEmptyValue}
                            idNumber={idNumber || defaultEmptyValue}
                            lastName={lastName || defaultEmptyValue}
                            addressOption={addressOption || defaultEmptyValue}/>;
        bottom = <StandAloneBottom key="bottom"
                                   isPosting={isPosting}
                                   handleSaveAddress={handleSaveAddress ? handleSaveAddress : () => {
                                   }}
                                   country={country.value}
                                   handleChange={handleChange}
                                   handleFocus={handleFocus}
                                   name={name || defaultEmptyValue}
                                   saveAddress={saveAddress}/>;
        break;
      case 'payout':
        top = <StandAloneTop key="top"
                             isPosting={isPosting}
                             handleChange={handleChange}
                             handleFocus={handleFocus}
                             country={country.value}
                             firstName={firstName || defaultEmptyValue}
                             idNumber={idNumber || defaultEmptyValue}
                             lastName={lastName || defaultEmptyValue}/>;
        break;
      case 'billing':
        top = <StandAloneTop key="top"
                             isPosting={isPosting}
                             handleChange={handleChange}
                             handleFocus={handleFocus}
                             country={country.value}
                             firstName={firstName || defaultEmptyValue}
                             idNumber={idNumber || defaultEmptyValue}
                             lastName={lastName || defaultEmptyValue}/>;
        break;
    }
    return [
      top,
      country.value &&
      <form key="middle"
            className={formStyle.formWrap}
            onSubmit={this.handleInnerSubmit.bind(this)}>
        <section className={classNames(formStyle.row, formStyle.inputRow)}>
          <FormattedMessage id={addressesIntlIds.address1.label}>{
            txt => <label className={classNames(formStyle.label,
              { [formStyle.readOnly]: readOnly })}>{txt}:</label>
          }</FormattedMessage>
          {!readOnly && <FormattedMessage id={addressesIntlIds.address1.placeholder}>{
            txt => <input disabled={isPosting}
                          className={classNames(inputStyle.input,
                            { [inputStyle.red]: address1.errors && address1.errors.length },
                            { [inputStyle.lightBlue]: !!address1.value })}
                          value={address1.value || ''}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          name="address1"
                          placeholder={txt}/>
          }</FormattedMessage>}
          {readOnly && <p className={formStyle.text}>{address1.value}</p>}
          {address1Error}
        </section>
        {(!readOnly || !!address2.value) &&
        <section className={classNames(formStyle.row, formStyle.inputRow)}>
          <FormattedMessage id={addressesIntlIds.address2.label}>{
            txt => <label className={classNames(formStyle.label,
              { [formStyle.readOnly]: readOnly })}>{txt}:</label>
          }</FormattedMessage>
          {!readOnly && <FormattedMessage id={addressesIntlIds.address2.placeholder}>{
            txt => <input disabled={isPosting}
                          className={classNames(inputStyle.input,
                            { [inputStyle.lightBlue]: !!address2.value })}
                          value={address2.value || ''}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          name="address2"
                          placeholder={txt}/>
          }</FormattedMessage>}
          {readOnly && <p className={formStyle.text}>{address2.value}</p>}
        </section>}
        <section className={classNames(formStyle.row, formStyle.inputRow,
          formStyle.dualWrap, formStyle.responsiveMedium)}>
          <article className={classNames(formStyle.col)}>
            <FormattedMessage id={addressesIntlIds.admin1.label}>{
              txt => <label className={classNames(formStyle.label,
                { [formStyle.readOnly]: readOnly })}>{txt}:</label>
            }</FormattedMessage>
            <Element name="admin1">
              {!readOnly && <FormattedMessage id={addressesIntlIds.admin1.placeholder}>{
                txt => <Select options={admin1Options}
                               className={classNames({
                                 'light-blue': admin1.value,
                                 'red': admin1.errors,
                               })}
                               value={admin1.value}
                               disabled={!admin1Options || !(admin1Options.length > 0) || (isPosting)}
                               searchable={true}
                               placeholder={txt}
                               handleChange={handleChangeAdmin1}/>
              }</FormattedMessage>}
              {!readOnly && isLoadingAdmin1 && <Loading type="sk-three-bounce"
                                                        number={3}
                                                        className={formStyle.loading}/>}
              {readOnly && <p className={formStyle.text}>{admin1.label}</p>}
              {admin1Error}
            </Element>
          </article>
          <article className={formStyle.col}>
            <FormattedMessage id={addressesIntlIds.admin2.label}>{
              txt => <label className={classNames(formStyle.label,
                { [formStyle.readOnly]: readOnly })}>{txt}:</label>
            }</FormattedMessage>
            <Element name="admin2">
              {!readOnly && <FormattedMessage id={addressesIntlIds.admin2.placeholder}>{
                txt => <Select options={admin2Options}
                               className={classNames({
                                 'light-blue': admin2.value,
                                 'red': admin2.errors,
                               })}
                               value={admin2.value}
                               disabled={!admin2Options || !(admin2Options.length > 0) || (isPosting)}
                               searchable={true}
                               placeholder={txt}
                               handleChange={handleChangeAdmin2}/>
              }</FormattedMessage>}
              {!readOnly && isLoadingAdmin2 && <Loading type="sk-three-bounce"
                                                        number={3}
                                                        className={classNames(formStyle.loading, formStyle.firstChild)}/>}
              {readOnly && <p className={formStyle.text}>{admin2.label}</p>}
              {admin2Error}
            </Element>
          </article>
        </section>
        <section className={classNames(formStyle.row, formStyle.inputRow,
          formStyle.dualWrap, formStyle.responsiveMedium, {
            [formStyle.rowNoMargin]: readOnly && type === 'courier',
          })}>
          {countryIsPE && addressesIntlIds.admin3 &&
          <article className={formStyle.col}>
            <FormattedMessage id={addressesIntlIds.admin3.label}>{
              txt => <label className={classNames(formStyle.label,
                { [formStyle.readOnly]: readOnly })}>{txt}:</label>
            }</FormattedMessage>
            <Element name="admin3">
              {!readOnly && <FormattedMessage id={addressesIntlIds.admin3.placeholder}>{
                txt =>
                  <Select options={admin3Options}
                          className={classNames({
                            'light-blue': admin3.value,
                            'red': admin3.errors,
                          })}
                          value={admin3.value}
                          disabled={!admin3Options || !(admin3Options.length > 0) || (isPosting)}
                          searchable={true}
                          placeholder={txt}
                          handleChange={handleChangeAdmin3}/>
              }</FormattedMessage>}
              {!readOnly && isLoadingAdmin3 && <Loading type="sk-three-bounce"
                                                        number={3}
                                                        className={formStyle.loading}/>}
              {readOnly && <p className={formStyle.text}>{admin3.label}</p>}
              {admin3Error}
            </Element>
          </article>}
          <article className={formStyle.col}>
            <FormattedMessage id={messages.zipCode.label.id}>{
              txt => <label className={classNames(formStyle.label,
                { [formStyle.readOnly]: readOnly })}>{txt}:</label>
            }</FormattedMessage>
            <Element name="zipCode">
              {!readOnly && <Cleave options={{ numericOnly: true, blocks: [5, 4], delimiter: ' ' }}
                                    disabled={isPosting}
                                    className={classNames(inputStyle.input,
                                      { [inputStyle.red]: zipCode && zipCode.errors && zipCode.errors.length },
                                      { [inputStyle.lightBlue]: zipCode && zipCode.value })}
                                    value={zipCode.value || ''}
                                    onChange={handleChange}
                                    onFocus={handleFocus}
                                    name="zipCode"
                                    placeholder={this.context.intl.formatMessage(messages.zipCode.placeholder)}/>}
              {readOnly && <p className={formStyle.text}>{zipCode.value}</p>}
              {zipCodeError}
            </Element>
          </article>
          {!countryIsPE &&
          <article className={classNames(formStyle.col)}>
            <FormattedMessage id={messages.phone.label.id}>{
              txt => <label className={classNames(formStyle.label,
                { [formStyle.readOnly]: readOnly })}>{txt}:</label>
            }</FormattedMessage>
            <Element name="phone">
              {!readOnly && <Cleave options={{ phone: true, phoneRegionCode: country.value || 'PE', delimiter: '-' }}
                                    disabled={isPosting || isPutting}
                                    className={classNames(inputStyle.input, { [inputStyle.red]: phone.errors && phone.errors.length },
                                      { [inputStyle.lightBlue]: !!phone.value })}
                                    onChange={handleChangePhone}
                                    onFocus={handleFocus}
                                    value={phone.value}
                                    name="phone"
                                    placeholder={this.context.intl.formatMessage(messages.phone.placeholder)}
                                    onInit={handlePhoneInit}/>}
              {readOnly && <p className={formStyle.text}>{phone.value}</p>}
              {phoneError}
            </Element>
          </article>}
        </section>
        {countryIsPE &&
        <section className={classNames(formStyle.row, formStyle.inputRow,
          formStyle.dualWrap, formStyle.responsiveMedium)}>
          <article className={classNames(formStyle.col)}>
            <FormattedMessage id={messages.phone.label.id}>{
              txt => <label className={classNames(formStyle.label,
                { [formStyle.readOnly]: readOnly })}>{txt}:</label>
            }</FormattedMessage>
            <Element name="phone">
              {!readOnly && <Cleave options={{ phone: true, phoneRegionCode: country.value || 'PE', delimiter: '-' }}
                                    disabled={isPosting || isPutting}
                                    className={classNames(inputStyle.input, { [inputStyle.red]: phone.errors && phone.errors.length },
                                      { [inputStyle.lightBlue]: !!phone.value })}
                                    onChange={handleChangePhone}
                                    onFocus={handleFocus}
                                    value={phone.value}
                                    name="phone"
                                    placeholder={this.context.intl.formatMessage(messages.phone.placeholder)}
                                    onInit={handlePhoneInit}/>}
              {readOnly && <p className={formStyle.text}>{phone.value}</p>}
              {phoneError}
            </Element>
          </article>
          {(type === 'myAddress' || type === 'standAlone') &&
          <article className={classNames(formStyle.col,
            { [style.disabled]: isPosting || !saveAddress })}>
            <FormattedMessage id={country.value === 'CO' ? messages.name.labelCO.id : messages.name.label.id}>{
              txt => <label className={formStyle.label}>{txt}:</label>
            }</FormattedMessage>
            <FormattedMessage id={messages.name.placeholder.id}>{
              txt => <input type="text"
                            className={classNames(inputStyle.input,
                              { [inputStyle.red]: name && name.errors && name.errors.length },
                              { [inputStyle.lightBlue]: !!name && !!name.value })}
                            value={!!name ? name.value : ''}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            name="name"
                            placeholder={txt}/>
            }</FormattedMessage>
          </article>}
        </section>}
      </form>,
      bottom,
    ];
  }
}

AddressForm.propTypes = {
  readOnly: PropTypes.bool,
  addresses: PropTypes.array,
  isPutting: PropTypes.bool,
  isPosting: PropTypes.bool,
  handleSaveAddress: PropTypes.func,
  handleChange: PropTypes.func.isRequired,
  handleChangeAddressOption: PropTypes.func,
  handleChangePhone: PropTypes.func.isRequired,
  handleFocus: PropTypes.func.isRequired,
  handleChangeCountry: PropTypes.func,
  handleChangeAdmin1: PropTypes.func.isRequired,
  handleChangeAdmin2: PropTypes.func.isRequired,
  handleChangeAdmin3: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func,
  handlePhoneInit: PropTypes.func.isRequired,
  country: PropTypes.object.isRequired,
  admin1: PropTypes.object.isRequired,
  admin1Items: PropTypes.array,
  isLoadingAdmin1: PropTypes.bool.isRequired,
  admin2: PropTypes.object.isRequired,
  admin2Items: PropTypes.array,
  isLoadingAdmin2: PropTypes.bool.isRequired,
  admin3: PropTypes.object.isRequired,
  admin3Items: PropTypes.array,
  isLoadingAdmin3: PropTypes.bool.isRequired,
  address1: PropTypes.object.isRequired,
  address2: PropTypes.object.isRequired,
  firstName: PropTypes.object,
  idNumber: PropTypes.object,
  lastName: PropTypes.object,
  name: PropTypes.object,
  phone: PropTypes.object.isRequired,
  saveAddress: PropTypes.bool,
  detail: PropTypes.object,
  type: PropTypes.string.isRequired,
  zipCode: PropTypes.object,
  localValues: PropTypes.object,
  addressOption: PropTypes.object,
};

AddressForm.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/elements/AddressForm/AddressForm.jsx